import { useEffect, useState } from 'react';
import MiniAppPage from '../components/MiniApp/MiniAppPage';
import MiniAppErrorBoundary from '../components/MiniApp/MiniAppErrorBoundary';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';

const MiniApp = () => {
  const { webApp, isTelegram } = useTelegramWebApp();
  const [isViewportReady, setIsViewportReady] = useState(false);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    console.log('📱 MiniApp useEffect triggered:', { isTelegram, webApp: !!webApp });

    // Enhanced initialization for Telegram only
    if (isTelegram && webApp) {
      try {
        // Add Telegram WebApp specific meta tags and viewport handling
        const addWebAppMeta = () => {
          // Enhanced viewport for Telegram WebApp with better mobile support
          let viewportMeta = document.querySelector('meta[name="viewport"]');
          if (!viewportMeta) {
            viewportMeta = document.createElement('meta');
            viewportMeta.setAttribute('name', 'viewport');
            document.head.appendChild(viewportMeta);
          }
          viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');

          // Telegram WebApp meta
          let webAppMeta = document.querySelector('meta[name="telegram-web-app"]');
          if (!webAppMeta) {
            webAppMeta = document.createElement('meta');
            webAppMeta.setAttribute('name', 'telegram-web-app');
            webAppMeta.setAttribute('content', 'true');
            document.head.appendChild(webAppMeta);
          }

          // Enhanced theme color for Telegram
          let themeMeta = document.querySelector('meta[name="theme-color"]');
          if (!themeMeta) {
            themeMeta = document.createElement('meta');
            themeMeta.setAttribute('name', 'theme-color');
            document.head.appendChild(themeMeta);
          }
          themeMeta.setAttribute('content', '#2d3748');

          // Apple mobile web app capable
          let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
          if (!appleMeta) {
            appleMeta = document.createElement('meta');
            appleMeta.setAttribute('name', 'apple-mobile-web-app-capable');
            appleMeta.setAttribute('content', 'yes');
            document.head.appendChild(appleMeta);
          }

          // Apple status bar style
          let appleStatusMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
          if (!appleStatusMeta) {
            appleStatusMeta = document.createElement('meta');
            appleStatusMeta.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
            appleStatusMeta.setAttribute('content', 'black-translucent');
            document.head.appendChild(appleStatusMeta);
          }
        };

        // Fix viewport height issues on mobile
        const fixViewportHeight = () => {
          const setVH = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
          };
          
          setVH();
          window.addEventListener('resize', setVH);
          
          return () => window.removeEventListener('resize', setVH);
        };

        console.log('Mini-app loading in Telegram WebApp environment');
        addWebAppMeta();
        cleanup = fixViewportHeight();
        
        // Add body class for Telegram styling
        document.body.classList.add('telegram-web-app');
        
        // Force proper background color
        document.body.style.backgroundColor = '#2d3748';
        document.documentElement.style.backgroundColor = '#2d3748';
        
        webApp.ready();
        webApp.expand();
        
        // Set header color to match app theme
        if (webApp.setHeaderColor) {
          webApp.setHeaderColor('#2d3748');
        }
        
        console.log('Telegram WebApp initialized successfully');
        
        // Mark viewport as ready after a short delay to ensure everything is loaded
        setTimeout(() => setIsViewportReady(true), 100);
      } catch (error) {
        console.error('Error initializing Telegram WebApp:', error);
        setIsViewportReady(true); // Still mark as ready to show the app
      }
    } else {
      // For non-Telegram environments, mark as ready immediately
      setIsViewportReady(true);
    }

    // Log initial parameters for debugging
    console.log('Mini-app initialization:', {
      isTelegram,
      userAgent: navigator.userAgent,
      urlParams: new URLSearchParams(window.location.search).toString(),
      hash: window.location.hash,
      href: window.location.href,
      telegramWebApp: !!window.Telegram?.WebApp,
      telegramUser: isTelegram && webApp && webApp.initDataUnsafe?.user ? webApp.initDataUnsafe.user.id : 'none',
      viewportReady: isViewportReady
    });

    return cleanup || (() => {});
  }, [isTelegram, webApp]);

  // Show loading screen while viewport is being set up
  if (!isViewportReady) {
    return (
      <div 
        className="min-h-screen bg-background flex items-center justify-center"
        style={{ backgroundColor: '#2d3748', minHeight: '100vh' }}
      >
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-foreground">Loading BroRankBot...</p>
        </div>
      </div>
    );
  }

  return (
    <MiniAppErrorBoundary>
      <div 
        className={`loading-fade ${isViewportReady ? 'loaded' : ''}`}
        style={{ backgroundColor: '#2d3748', minHeight: '100vh' }}
      >
        <MiniAppPage />
      </div>
    </MiniAppErrorBoundary>
  );
};

export default MiniApp;